import type { JsonSchemaType } from '@gmn-portal/backend';
import { zodResolver } from '@hookform/resolvers/zod';
import { FileSignature } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { useContract, useCreateContract, useUserGroups } from '@/core/api';
import type { PayloadApiError } from '@/core/api/client';
import { useCustomer } from '@/core/api/customers';
import { useGlobalMetadata } from '@/core/api/global-metadata';
import { useObjects } from '@/core/api/objects';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { useScrollSpy } from '@/features/shared/objects/components/use-scroll-spy';
import { DetailPageLayout } from '@/shared/components/layout';
import {
  Form,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui';
import { useUnsavedChangesGuard } from '@/shared/hooks/use-unsaved-changes-guard';
import { scrollToTarget } from '@/shared/utils';
import { createMetadataZodSchema, sanitizeMetadataValues } from '@/shared/utils/metadata';
import { canReadService, resolveUserGroup } from '@/shared/utils/permissions';
import { ContractCreateBillingSection } from '../components/contract-create-sections';
import { ContractDetailsSidemenu } from '../components/contract-details-sidemenu';
import {
  buildContractMetadataSections,
  ContractMasterDataSection,
  ContractMetadataSections,
} from '../components/contract-general-sections';
import { ContractMetadataBuilderDialog } from '../components/contract-metadata-builder-dialog';
import { createContractSchema } from '../schema/contract-schema';
import type { ContractFormData } from '../types/contract-form';

const emptyDefaults: ContractFormData = {
  orderNumber: '',
  weaOssCount: undefined,
  title: '',
  customer: '',
  objects: [],
  startDate: '',
  endDate: '',
  note: '',
  status: 'open',
  paymentTermsDays: 30,
  billingTotalCents: 0,
  billingInterval: 'monthly',
  billingGenerationMode: 'generated',
  billingPoNumber: '',
  billingContactName: '',
  billingContactEmail: '',
  billingAddress: '',
  billingPeriods: [],
  metadata: {},
  dynamicMetadataSchema: undefined,
  dynamicMetadataValues: {},
};

export function ContractCreateWizard() {
  const { t, locale } = useIntl();
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const group = resolveUserGroup(user);
  const groupKey = group?.key ?? null;
  const isGmnAdmin = groupKey === 'gmn-admin';
  const canReadObjects = group ? canReadService(group, 'objects') : false;
  const basePath = '/gmn/contract-management/contracts';
  const masterDataSectionId = 'contract-master-data';
  const billingSectionId = 'contract-billing-settings';

  const parentId = searchParams.get('parentId') || undefined;
  const isSubcontractCreate = Boolean(parentId);

  const createContract = useCreateContract();
  const { data: globalMetadata } = useGlobalMetadata();
  const contractMetadataConfig = globalMetadata?.rootContractMetadata as JsonSchemaType | undefined;
  const { data: userGroupsData } = useUserGroups({ limit: 0, sort: 'key' });
  const userGroups = userGroupsData?.docs ?? [];

  const { data: parentContract } = useContract(parentId || '', {
    disabled: !isSubcontractCreate,
    depth: 1,
  });

  const parentCustomerId = useMemo(() => {
    if (!parentContract) return undefined;
    const customer = (parentContract as any).customer;
    return typeof customer === 'string' ? customer : customer?.id;
  }, [parentContract]);

  const [error, setError] = useState<PayloadApiError | undefined>();
  const [metadataSchemaZod, setMetadataSchemaZod] = useState<z.ZodObject<z.ZodRawShape>>(
    z.object({})
  );
  const [dynamicMetadataSchemaZod, setDynamicMetadataSchemaZod] = useState<
    z.ZodObject<z.ZodRawShape>
  >(z.object({}));

  const [isMetadataDrawerOpen, setIsMetadataDrawerOpen] = useState(false);
  const [drawerSchema, setDrawerSchema] = useState<JsonSchemaType | undefined>(undefined);
  const [createdContractId, setCreatedContractId] = useState<string | null>(null);

  const initialValuesRef = useRef<ContractFormData>(emptyDefaults);

  const form = useForm<ContractFormData, any, ContractFormData>({
    resolver: zodResolver(
      createContractSchema(t, metadataSchemaZod, dynamicMetadataSchemaZod, {
        requireBilling: false,
      })
    ) as any,
    defaultValues: initialValuesRef.current,
  });

  useEffect(() => {
    if (!contractMetadataConfig) return;
    setMetadataSchemaZod(createMetadataZodSchema(contractMetadataConfig, groupKey));
  }, [contractMetadataConfig, groupKey]);

  useEffect(() => {
    if (!globalMetadata) return;
    const staticSchema = globalMetadata.rootContractMetadata as JsonSchemaType | undefined;
    const currentMeta = (form.getValues().metadata || {}) as Record<string, unknown>;
    const sanitizedMeta = sanitizeMetadataValues(currentMeta, staticSchema);
    if (JSON.stringify(currentMeta) !== JSON.stringify(sanitizedMeta)) {
      form.setValue('metadata', sanitizedMeta, { shouldValidate: false, shouldDirty: false });
    }

    const currentDynValues = (form.getValues().dynamicMetadataValues || {}) as Record<
      string,
      unknown
    >;
    const sanitizedDyn = sanitizeMetadataValues(
      currentDynValues,
      form.getValues().dynamicMetadataSchema as JsonSchemaType
    );
    if (JSON.stringify(currentDynValues) !== JSON.stringify(sanitizedDyn)) {
      form.setValue('dynamicMetadataValues', sanitizedDyn, {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, [globalMetadata, form, metadataSchemaZod, dynamicMetadataSchemaZod]);

  useEffect(() => {
    if (!error?.response?.errors) return;
    toast.error(t('common.errorSaving'));
  }, [error, t]);

  useEffect(() => {
    if (!createdContractId) return;
    navigate(`${basePath}/${createdContractId}/general`);
  }, [basePath, createdContractId, navigate]);

  useEffect(() => {
    if (!isSubcontractCreate) return;
    if (!parentCustomerId) return;
    if (form.getValues().customer === parentCustomerId) return;
    form.setValue('customer', parentCustomerId, { shouldDirty: false, shouldValidate: true });
  }, [form, isSubcontractCreate, parentCustomerId]);

  const discardChanges = () => {
    form.reset(initialValuesRef.current);
    setError(undefined);
    setDynamicMetadataSchemaZod(z.object({}));
    setDrawerSchema(undefined);
    setIsMetadataDrawerOpen(false);
    setCreatedContractId(null);
  };

  const { dialog: unsavedDialog } = useUnsavedChangesGuard({
    when: form.formState.isDirty && !createdContractId,
    onDiscard: discardChanges,
  });

  const openMetadataDrawer = () => {
    setDrawerSchema(form.getValues().dynamicMetadataSchema);
    setIsMetadataDrawerOpen(true);
  };

  const handleMetadataBuilderSave = () => {
    form.setValue('dynamicMetadataSchema', drawerSchema, {
      shouldDirty: true,
      shouldValidate: true,
    });
    if (drawerSchema) {
      setDynamicMetadataSchemaZod(createMetadataZodSchema(drawerSchema, groupKey));
    } else {
      setDynamicMetadataSchemaZod(z.object({}));
    }
    setIsMetadataDrawerOpen(false);
  };

  const selectedCustomerId = form.watch('customer') || '';
  const { data: selectedCustomer } = useCustomer(selectedCustomerId);

  const selectedObjectIds = form.watch('objects') || [];
  const { data: selectedObjectsData, isLoading: isLoadingObjects } = useObjects({
    where: { id: { in: selectedObjectIds } },
    limit: selectedObjectIds.length,
    disabled: selectedObjectIds.length === 0,
  });

  const aggregatedObjects = useMemo(
    () => (selectedObjectsData?.docs || []).map((o) => ({ id: o.id, name: o.name })),
    [selectedObjectsData?.docs]
  );

  const submitCreate = async (data: ContractFormData) => {
    try {
      setError(undefined);
      if (isSubcontractCreate && (!parentId || !parentContract || !parentCustomerId)) {
        toast.error(t('contracts.invalidParentContract'));
        return;
      }

      const response = await createContract.mutateAsync(
        isSubcontractCreate
          ? ({
              ...data,
              customer: parentCustomerId!,
              parentContract: parentId,
            } as any)
          : (data as any)
      );
      form.reset(data);
      toast.success(t('common.savedSuccessfully'));
      setCreatedContractId(response.doc.id);
    } catch (err) {
      setError(err as PayloadApiError);
      toast.error(t('common.errorSaving'));
    }
  };

  const metadataValues = (form.watch('metadata') || {}) as Record<string, unknown>;
  const customValues = (form.watch('dynamicMetadataValues') || {}) as Record<string, unknown>;
  const dynamicSchema = form.watch('dynamicMetadataSchema') as JsonSchemaType | undefined;

  const predefinedMetadataSections = useMemo(
    () =>
      buildContractMetadataSections({
        schema: contractMetadataConfig,
        locale,
        ungroupedLabel: t('metadata.builder.ungrouped'),
        groupIdPrefix: 'contract-metadata-predefined-group',
        ungroupedId: 'contract-metadata-predefined-ungrouped',
        groupKey,
      }),
    [contractMetadataConfig, groupKey, locale, t]
  );

  const customMetadataSections = useMemo(
    () =>
      buildContractMetadataSections({
        schema: dynamicSchema,
        locale,
        ungroupedLabel: t('metadata.builder.ungrouped'),
        groupIdPrefix: 'contract-metadata-custom-group',
        ungroupedId: 'contract-metadata-custom-ungrouped',
        groupKey,
      }),
    [dynamicSchema, groupKey, locale, t]
  );

  const hasAnyErrorForFields = (names: string[]) => {
    const errors = form.formState.errors as any;
    return names.some((name) => Boolean(errors?.[name]));
  };

  const hasMetadataErrors = (path: 'metadata' | 'dynamicMetadataValues', fieldNames: string[]) => {
    const errors = form.formState.errors as any;
    const scoped = errors?.[path] || {};
    return fieldNames.some((name) => Boolean(scoped?.[name]));
  };

  const sidemenuItems = useMemo(() => {
    const masterDataFields: string[] = [
      'title',
      'orderNumber',
      'status',
      'startDate',
      'endDate',
      'customer',
      'objects',
      'note',
    ];

    const masterDataItems = [
      {
        id: masterDataSectionId,
        label: t('contracts.sections.masterData'),
        kind: 'master-data' as const,
        hasError: hasAnyErrorForFields(masterDataFields),
      },
      {
        id: billingSectionId,
        label: t('contracts.billingSettings'),
        kind: 'master-data' as const,
        hasError: hasAnyErrorForFields([
          'billingTotalCents',
          'billingInterval',
          'paymentTermsDays',
          'billingPoNumber',
          'billingContactName',
          'billingContactEmail',
          'billingAddress',
        ]),
      },
    ];

    const predefinedItems = predefinedMetadataSections.map((section) => ({
      id: section.id,
      label: section.label,
      kind: 'metadata-group' as const,
      hasError: hasMetadataErrors('metadata', section.fieldNames),
    }));

    const customItems = customMetadataSections.map((section) => ({
      id: section.id,
      label: section.label,
      kind: 'custom-group' as const,
      hasError: hasMetadataErrors('dynamicMetadataValues', section.fieldNames),
    }));

    return [...masterDataItems, ...predefinedItems, ...customItems];
  }, [customMetadataSections, predefinedMetadataSections, t, form.formState.errors]);

  const activeSectionId = useScrollSpy(
    sidemenuItems.map((i) => i.id),
    { root: scrollRoot, rootMargin: '-15% 0px -75% 0px' }
  );

  const scrollToSection = (id: string) => {
    scrollToTarget(id, { root: scrollRoot });
  };

  useEffect(() => {
    if (typeof document === 'undefined') return;
    setScrollRoot(document.querySelector('main.overflow-y-auto') as HTMLElement | null);
  }, []);

  return (
    <DetailPageLayout
      containerProps={{ animation: 'slide-right' }}
      headerProps={{
        backHref: isSubcontractCreate ? `${basePath}/${parentId}/subcontracts` : basePath,
        backLabel: isSubcontractCreate
          ? t('contracts.backToContract')
          : t('contracts.backToOverview'),
        title: isSubcontractCreate ? t('contracts.newSubcontract') : t('contracts.newContract'),
        subtitle: isSubcontractCreate
          ? t('contracts.createNewSubcontractHint')
          : t('contracts.createNewContractHint'),
        icon: FileSignature,
        isCreateMode: true,
        secondaryAction: {
          label: t('common.discard'),
          onClick: () =>
            navigate(isSubcontractCreate ? `${basePath}/${parentId}/subcontracts` : basePath),
        },
        primaryAction: {
          label: createContract.isPending ? t('common.creating') : t('common.create'),
          onClick: () => void form.handleSubmit(submitCreate)(),
          disabled: createContract.isPending || (isSubcontractCreate && !parentCustomerId),
          loading: createContract.isPending,
        },
      }}
    >
      <Form {...form}>
        <form
          id="contract-create-form"
          onSubmit={form.handleSubmit(submitCreate)}
          className="grid gap-6"
        >
          <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />

          <div className="grid gap-6 lg:grid-cols-12">
            <div className="lg:col-span-3">
              <div className="lg:hidden">
                <Select
                  value={activeSectionId ?? sidemenuItems[0]?.id ?? masterDataSectionId}
                  onValueChange={scrollToSection}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sidemenuItems.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="hidden lg:block sticky top-4 self-start max-h-[calc(100vh-2rem)] overflow-auto">
                <ContractDetailsSidemenu
                  items={sidemenuItems}
                  activeId={activeSectionId}
                  onSelect={scrollToSection}
                  showCustomSeparator={customMetadataSections.length > 0}
                />
              </div>
            </div>

            <div className="lg:col-span-9 grid gap-10">
              <ContractMasterDataSection
                id={masterDataSectionId}
                mode="edit"
                form={form}
                canEditContracts={true}
                canReadObjects={canReadObjects}
                disableCustomerSelection={isSubcontractCreate}
                customerName={selectedCustomer?.name}
                aggregatedObjectIds={selectedObjectIds}
                aggregatedObjects={aggregatedObjects}
                isLoadingObjects={isLoadingObjects}
              />

              <ContractMetadataSections
                mode="edit"
                form={form}
                predefinedSchema={contractMetadataConfig}
                predefinedZod={metadataSchemaZod}
                predefinedValues={metadataValues}
                predefinedSections={predefinedMetadataSections}
                dynamicSchema={dynamicSchema}
                dynamicZod={dynamicMetadataSchemaZod}
                dynamicValues={customValues}
                dynamicSections={customMetadataSections}
                isGmnAdmin={isGmnAdmin}
                onEditDynamicSchema={openMetadataDrawer}
                groupKey={groupKey}
              />

              <ContractCreateBillingSection id={billingSectionId} form={form} />
            </div>
          </div>
        </form>
      </Form>

      <ContractMetadataBuilderDialog
        open={isMetadataDrawerOpen}
        value={drawerSchema}
        onChange={setDrawerSchema}
        onCancel={() => setIsMetadataDrawerOpen(false)}
        onSave={handleMetadataBuilderSave}
        userGroups={userGroups}
      />

      {unsavedDialog}
    </DetailPageLayout>
  );
}
